import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface CanvasImage {
    id: string;
    src: string;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
    originalX?: number;
    originalY?: number;
    draggable?: boolean;
}

export interface CanvasText {
    id: string;
    text: string;
    x: number;
    y: number;
    width: number;
    height: number;
    fontSize: string;
    color: string;
    fontFamily: string;
    effect?: string; // Text effect: none, shadow, hallow, background, curve, outline, neon, glitch
    rotation: number;
}

export interface CanvasVideo {
    id: string;
    src: string;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
}

export interface TimerState {
    timeLeft: number;
    duration: number;
    isRunning: boolean;
    startTime: number | null;
    hasExtended: boolean;
    isInitialized: boolean;
}

export interface CanvasDrawing {
    id: string;
    dataUrl: string;
    width: number;
    height: number;
    timestamp: number;
}

interface CanvasState {
    images: CanvasImage[];
    texts: CanvasText[];
    videos: CanvasVideo[];
    drawings: CanvasDrawing[];
    timer: TimerState;
    history: {
        past: Array<{
            images: CanvasImage[];
            texts: CanvasText[];
            videos: CanvasVideo[];
            drawings: CanvasDrawing[];
        }>;
        future: Array<{
            images: CanvasImage[];
            texts: CanvasText[];
            videos: CanvasVideo[];
            drawings: CanvasDrawing[];
        }>;
    };
}

const getTimerDuration = (): number => {
  const envValue = process.env.REACT_APP_TIMER_DURATION_MINUTES;
  if (!envValue) return 15 * 60; // Default: 15 minutes in seconds
  const parsed = parseFloat(envValue); // Use parseFloat to support decimals
  if (isNaN(parsed) || parsed <= 0) return 15 * 60; // Fallback to 15 minutes if invalid
  return Math.round(parsed * 60); // Convert minutes to seconds and round
};

const TIMER_DURATION = getTimerDuration();
console.log('Timer Duration Configuration:', {
  envValue: process.env.REACT_APP_TIMER_DURATION_MINUTES,
  durationSeconds: TIMER_DURATION,
  durationMinutes: TIMER_DURATION / 60,
  durationFormatted: `${Math.floor(TIMER_DURATION / 60)}:${String(TIMER_DURATION % 60).padStart(2, '0')}`
});

const initialState: CanvasState = {
    images: [],
    texts: [],
    videos: [],
    drawings: [],
    timer: {
        timeLeft: TIMER_DURATION,
        duration: TIMER_DURATION,
        isRunning: false,
        startTime: null,
        hasExtended: false,
        isInitialized: false,
    },
    history: {
        past: [],
        future: [],
    },
};

// Migration function to ensure all text elements have width and height
const migrateTextElements = (texts: any[]): any[] => {
    return texts.map(text => ({
        ...text,
        width: text.width || 200,
        height: text.height || 50,
    }));
};

// Helper function to save current state to history
const saveToHistory = (state: CanvasState) => {
    const currentState = {
        images: [...state.images],
        texts: [...state.texts],
        videos: [...state.videos],
        drawings: [...state.drawings],
    };
    
    state.history.past.push(currentState);
    // Clear future history when new action is performed
    state.history.future = [];
    
    // Limit history to 50 states to prevent memory issues
    if (state.history.past.length > 50) {
        state.history.past.shift();
    }
};

const canvasSlice = createSlice({
    name: "canvas",
    initialState,
    reducers: {
        // Migration action to fix existing text elements
        migrateTexts: (state) => {
            state.texts = migrateTextElements(state.texts);
        },
        // ✅ Image Actions
        addImage: (state, action: PayloadAction<Omit<CanvasImage, "id">>) => {
            if (!state.images.some((img) => img.src === action.payload.src)) {
                saveToHistory(state);
                const newImage: CanvasImage = {
                    id: Date.now().toString(),
                    ...action.payload,
                };
                state.images.push(newImage);
            }
        },
        updateImage: (state, action: PayloadAction<{ id: string; changes: Partial<CanvasImage> }>) => {
            const image = state.images.find((img) => img.id === action.payload.id);
            if (image) {
                saveToHistory(state);
                Object.assign(image, action.payload.changes);
            }
        },
        deleteImage: (state, action: PayloadAction<string>) => {
            saveToHistory(state);
            state.images = state.images.filter((img) => img.id !== action.payload);
        },

        // ✅ Text Actions
        addText: (state, action: PayloadAction<Omit<CanvasText, "id" | "x" | "y" | "rotation">>) => {
            saveToHistory(state);
            state.texts.push({
                ...action.payload,
                id: Date.now().toString(),
                x: 100,
                y: 100,
                rotation: 0,
                width: action.payload.width || 200, // Default width if not provided
                height: action.payload.height || 50, // Default height if not provided
                effect: action.payload.effect || "none", // Default to "none" if not provided
            });
        },
        updateText: (state, action: PayloadAction<{ id: string; changes: Partial<CanvasText> }>) => {
            const text = state.texts.find((txt) => txt.id === action.payload.id);
            if (text) {
                saveToHistory(state);
                Object.assign(text, action.payload.changes);
            }
        },
        deleteText: (state, action: PayloadAction<string>) => {
            saveToHistory(state);
            state.texts = state.texts.filter((txt) => txt.id !== action.payload);
        },

        // ✅ Video Actions
        addVideo: (state, action: PayloadAction<Omit<CanvasVideo, "id">>) => {
            if (!state.videos.some((vid) => vid.src === action.payload.src)) {
                saveToHistory(state);
                const newVideo: CanvasVideo = {
                    id: Date.now().toString(),
                    ...action.payload,
                };
                state.videos.push(newVideo);
            }
        },
        updateVideo: (state, action: PayloadAction<{ id: string; changes: Partial<CanvasVideo> }>) => {
            const video = state.videos.find((vid) => vid.id === action.payload.id);
            if (video) {
                saveToHistory(state);
                Object.assign(video, action.payload.changes);
            }
        },
        deleteVideo: (state, action: PayloadAction<string>) => {
            saveToHistory(state);
            state.videos = state.videos.filter((vid) => vid.id !== action.payload);
        },

        // ✅ Drawing Actions
        saveDrawing: (state, action: PayloadAction<Omit<CanvasDrawing, "id" | "timestamp">>) => {
            saveToHistory(state);
            const newDrawing: CanvasDrawing = {
                id: Date.now().toString(),
                timestamp: Date.now(),
                ...action.payload,
            };
            // Replace existing drawing or add new one
            const existingIndex = state.drawings.findIndex(d => d.id === newDrawing.id);
            if (existingIndex >= 0) {
                state.drawings[existingIndex] = newDrawing;
            } else {
                state.drawings.push(newDrawing);
            }
        },
        clearDrawings: (state) => {
            saveToHistory(state);
            state.drawings = [];
        },

        // ✅ Undo/Redo Actions
        undo: (state) => {
            if (state.history.past.length > 0) {
                const previousState = state.history.past[state.history.past.length - 1];
                const currentState = {
                    images: [...state.images],
                    texts: [...state.texts],
                    videos: [...state.videos],
                    drawings: [...state.drawings],
                };
                
                // Move current state to future
                state.history.future.push(currentState);
                
                // Restore previous state
                state.images = [...previousState.images];
                state.texts = [...previousState.texts];
                state.videos = [...previousState.videos];
                state.drawings = [...previousState.drawings];
                
                // Remove from past
                state.history.past.pop();
            }
        },
        redo: (state) => {
            if (state.history.future.length > 0) {
                const nextState = state.history.future[state.history.future.length - 1];
                const currentState = {
                    images: [...state.images],
                    texts: [...state.texts],
                    videos: [...state.videos],
                    drawings: [...state.drawings],
                };
                
                // Move current state to past
                state.history.past.push(currentState);
                
                // Restore next state
                state.images = [...nextState.images];
                state.texts = [...nextState.texts];
                state.videos = [...nextState.videos];
                state.drawings = [...nextState.drawings];
                
                // Remove from future
                state.history.future.pop();
            }
        },
        resetCanvas: (state) => {
            saveToHistory(state);
            state.images = [];
            state.texts = [];
            state.videos = [];
            state.drawings = [];
        },
        clearCanvas: (state) => {
            state.images = [];
            state.texts = [];
            state.videos = [];
            state.drawings = [];
            state.history.past = [];
            state.history.future = [];
        },
        // Function to clear only specific items while keeping others
        clearCanvasItems: (state, action: PayloadAction<{ images?: boolean; texts?: boolean; videos?: boolean; drawings?: boolean }>) => {
            const { images, texts, videos, drawings } = action.payload;
            if (images) {
                state.images = [];
            }
            if (texts) {
                state.texts = [];
            }
            if (videos) {
                state.videos = [];
            }
            if (drawings) {
                state.drawings = [];
            }
            // Clear history when items are cleared
            if (images || texts || videos || drawings) {
                state.history.past = [];
                state.history.future = [];
            }
        },

        // ✅ Timer Actions
        initializeTimer: (state) => {
            if (!state.timer.isInitialized) {
                state.timer.isInitialized = true;
                state.timer.timeLeft = TIMER_DURATION;
                state.timer.duration = TIMER_DURATION;
                state.timer.isRunning = false;
                state.timer.startTime = null;
                state.timer.hasExtended = false;
            }
        },
        startTimer: (state) => {
            if (!state.timer.isRunning) {
                state.timer.isRunning = true;
                state.timer.startTime = Date.now();
            }
        },
        updateTimer: (state) => {
            if (state.timer.isRunning && state.timer.startTime) {
                const now = Date.now();
                const elapsed = Math.floor((now - state.timer.startTime) / 1000);
                const newTimeLeft = Math.max(0, state.timer.duration - elapsed);
                
                state.timer.timeLeft = newTimeLeft;
                
                // Auto-stop when timer reaches 0
                if (newTimeLeft <= 0) {
                    state.timer.isRunning = false;
                    state.timer.startTime = null;
                }
            }
        },
        pauseTimer: (state) => {
            state.timer.isRunning = false;
            state.timer.startTime = null;
        },
        extendTimer: (state, action: PayloadAction<number>) => {
            const extraTime = action.payload; // in seconds
            // Set new duration to just the extension time
            state.timer.duration = extraTime;
            state.timer.timeLeft = extraTime;
            state.timer.hasExtended = true;
            // Restart timer with fresh start time
            state.timer.isRunning = true;
            state.timer.startTime = Date.now();
        },
        resetTimer: (state) => {
            state.timer.timeLeft = TIMER_DURATION;
            state.timer.duration = TIMER_DURATION;
            state.timer.isRunning = false;
            state.timer.startTime = null;
            state.timer.hasExtended = false;
            state.timer.isInitialized = false; // Reset to allow re-initialization
        },
        setTimerTimeLeft: (state, action: PayloadAction<number>) => {
            state.timer.timeLeft = action.payload;
        },
    },
});

export const {
    migrateTexts,
    addImage,
    updateImage,
    deleteImage,
    addText,
    updateText,
    deleteText,
    addVideo,
    updateVideo,
    deleteVideo,
    saveDrawing,
    clearDrawings,
    undo,
    redo,
    resetCanvas,
    clearCanvas,
    clearCanvasItems,
    // Timer actions
    initializeTimer,
    startTimer,
    updateTimer,
    pauseTimer,
    extendTimer,
    resetTimer,
    setTimerTimeLeft,
} = canvasSlice.actions;

export default canvasSlice.reducer;
